/**
 * Watermark chống lộ trên trang Quản trị: lớp nền chữ mờ phủ TOÀN cửa sổ (kể
 * cả Modal/Drawer/thông báo của antd — z-index tối đa), mang tên · @TikTok ID +
 * mã truy vết + ngày giờ của người đang xem. Ảnh chụp màn hình bị lộ → đọc mã →
 * "Watermark chống lộ → Tra mã". Tài liệu: docs/watermark/README.md.
 *
 * Chống gỡ kiểu antd Watermark: node gắn thẳng vào <body> với style inline
 * !important; MutationObserver thấy bị xoá/sửa (DevTools) là gắn lại ngay.
 * Chỉ là rào cản cho người dùng thường — ai tắt JavaScript/sửa mã thì vẫn gỡ
 * được, nên mã truy vết còn in trong lớp ẩn và trên app.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AxiosUtils from 'utils/axios-utils';
import { findNav } from 'layouts/nav-config';
import { API } from 'service/api-route';
import { WatermarkPayload, watermarkBackground } from './render';

/** Trang cấu hình phát sự kiện này sau khi lưu → lớp phủ tải lại ngay. */
export const WATERMARK_CHANGED_EVENT = 'hp-watermark-changed';

const REFRESH_MS = 2 * 60 * 1000;
const NODE_ATTR = 'data-hp-wm';

const useMinuteClock = (enabled: boolean) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!enabled) return undefined;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const current = new Date();
      setNow(current);
      timer = setTimeout(tick, 60_000 - (current.getTime() % 60_000) + 50);
    };
    tick();
    return () => clearTimeout(timer);
  }, [enabled]);
  return now;
};

/** Font Be Vietnam Pro tải xong thì vẽ lại (canvas không tự vẽ lại như DOM). */
const useFontsReady = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const fonts = (document as any).fonts;
    if (!fonts?.ready) {
      setReady(true);
      return undefined;
    }
    fonts.ready.then(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, []);
  return ready;
};

/** Kéo cửa sổ sang màn hình khác độ phân giải / phóng to trang → vẽ lại cho nét. */
const useDevicePixelRatio = () => {
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  useEffect(() => {
    const onResize = () => setDpr(window.devicePixelRatio || 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return dpr;
};

const buildCss = (image: string, size: string) =>
  [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'bottom:0',
    'width:auto',
    'height:auto',
    'margin:0',
    'padding:0',
    'z-index:2147483647',
    'pointer-events:none',
    'user-select:none',
    'display:block',
    'visibility:visible',
    'opacity:1',
    'transform:none',
    'filter:none',
    'clip-path:none',
    'mix-blend-mode:normal',
    'background-color:transparent',
    `background-image:${image}`,
    `background-size:${size}`,
    'background-repeat:repeat',
    'background-position:0 0',
    // In trang (Ctrl+P) cũng giữ watermark.
    '-webkit-print-color-adjust:exact',
    'print-color-adjust:exact',
  ]
    .map((rule) => `${rule} !important`)
    .join(';');

/** Gắn lớp phủ vào <body> và giữ nó ở đó cho tới khi unmount. */
const usePinnedOverlay = (css: string | null) => {
  useEffect(() => {
    if (!css) return undefined;
    let node: HTMLDivElement | null = null;
    let applied = '';
    let frame = 0;

    const ensure = () => {
      frame = 0;
      if (!node || node.parentNode !== document.body) {
        node?.remove();
        node = document.createElement('div');
        node.setAttribute(NODE_ATTR, '');
        node.setAttribute('aria-hidden', 'true');
        document.body.appendChild(node);
        observer.observe(node, { attributes: true });
        applied = '';
      }
      if (!applied || node.style.cssText !== applied) {
        node.removeAttribute('class');
        node.removeAttribute('hidden');
        node.style.cssText = css;
        applied = node.style.cssText;
      }
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(ensure);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true });
    ensure();
    // Phòng khi observer bị vô hiệu (vd. body bị thay): kiểm lại định kỳ.
    const interval = window.setInterval(schedule, 3000);

    return () => {
      observer.disconnect();
      window.clearInterval(interval);
      if (frame) window.cancelAnimationFrame(frame);
      node?.remove();
    };
  }, [css]);
};

const WatermarkOverlay = () => {
  const [payload, setPayload] = useState<WatermarkPayload | null>(null);
  const fontsReady = useFontsReady();
  const dpr = useDevicePixelRatio();
  const now = useMinuteClock(!!payload?.enabled && !!payload.showTime);

  useEffect(() => {
    let alive = true;
    let lastFetch = 0;
    const load = () => {
      lastFetch = Date.now();
      AxiosUtils.get(API.watermarkMe, { surface: 'admin' })
        .then((res: any) => {
          const data = res?.data;
          if (alive && typeof data?.enabled === 'boolean') setPayload(data);
        })
        .catch((error: any) => {
          // Server cũ chưa có API → tắt; lỗi mạng → giữ lớp phủ đang có.
          const status = error?.statusCode || error?.status || error?.response?.status;
          if (alive && status === 404) setPayload(null);
        });
    };
    load();
    const onFocus = () => {
      if (Date.now() - lastFetch > REFRESH_MS) load();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(WATERMARK_CHANGED_EVENT, load);
    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(WATERMARK_CHANGED_EVENT, load);
    };
  }, []);

  // Vị trí = nhóm menu của trang đang mở ("home" = Tổng quan); admin tắt được từng nhóm.
  const location = useLocation();
  const match = findNav(location.pathname);
  const position = match?.group?.key ?? (match ? 'home' : 'other');
  const off = !!payload?.offPositions?.includes(position);

  const background = payload && fontsReady && !off ? watermarkBackground(payload, now, dpr) : null;
  usePinnedOverlay(background ? buildCss(background.image, background.size) : null);
  return null;
};

export default WatermarkOverlay;
